import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Footer from "./components/Footer";
import { getBasket, addToBasket } from "./code/basket";
import { loadStripe } from "@stripe/stripe-js";
import "../css/Checkout.css";
import { FaTrashCan } from "react-icons/fa6";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

function Checkout({ stock: initialStock }) {
  const [basket, setBasket] = useState(getBasket());
  const [stock, setStock] = useState(initialStock || {});
  const [stockAdjusted, setStockAdjusted] = useState(false);

  // Watch for basket changes
  useEffect(() => {
    const handleUpdate = () => setBasket(getBasket());
    window.addEventListener("basketUpdated", handleUpdate);
    return () => window.removeEventListener("basketUpdated", handleUpdate);
  }, []);

  // ✅ Re-fetch stock when checkout opens
  useEffect(() => {
    async function refreshStock() {
      try {
        const res = await fetch("/.netlify/functions/get-stock-supabase", {
          cache: "no-store",
        });
        const latestStock = await res.json();
        setStock(latestStock);

        // Compare basket items with latest stock
        let adjusted = false;
        const updatedBasket = basket
          .map((item) => {
            const available = latestStock[item.id] ?? 0;

            if (available === 0) {
              adjusted = true;
              return null; // remove item
            }
            if (item.quantity > available) {
              adjusted = true;
              return { ...item, quantity: available }; // reduce quantity
            }
            return item;
          })
          .filter(Boolean);

        if (adjusted) {
          setStockAdjusted(true);
          localStorage.setItem("basket", JSON.stringify(updatedBasket));
          window.dispatchEvent(new Event("basketUpdated"));
          setBasket(updatedBasket);
        }
      } catch (err) {
        console.error("Error refreshing stock:", err);
      }
    }

    refreshStock();
  }, [basket]); // only when checkout first loads

  // Quantity functions
  const incrementQuantity = (priceId, itemId) => {
    const itemStock = stock[itemId] ?? 0;
    const currentItem = basket.find((item) => item.priceId === priceId);

    if (currentItem && currentItem.quantity < itemStock) {
      addToBasket(null, priceId, null, 1);
      setBasket(getBasket());
    }
  };

  const decrementQuantity = (priceId) => {
    const updated = basket.map((item) => {
      if (item.priceId === priceId) {
        return { ...item, quantity: Math.max(1, item.quantity - 1) };
      }
      return item;
    });
    localStorage.setItem("basket", JSON.stringify(updated));
    window.dispatchEvent(new Event("basketUpdated"));
  };

  const removeItem = (priceId) => {
    const updated = basket.filter((item) => item.priceId !== priceId);
    localStorage.setItem("basket", JSON.stringify(updated));
    window.dispatchEvent(new Event("basketUpdated"));
  };

  const totalCost = basket.reduce(
    (sum, item) => sum + (item.price || 0) * item.quantity,
    0
  );

  const checkoutFunction = async () => {
    const stripe = await stripePromise;

    const res = await fetch("/.netlify/functions/create-checkout", {
      method: "POST",
      body: JSON.stringify({ 
        basket,
        origin: window.location.origin 
      }),
    });

    const { sessionId } = await res.json();

    console.log(sessionId);

    const { error } = await stripe.redirectToCheckout({ sessionId });
    if (error) alert(error.message);
  };

  // Animation variants
  const slideVariants = {
    initial: { x: "100%" },
    animate: { x: 0, transition: { type: "spring", stiffness: 300, damping: 30 } },
    exit: { x: "100%", transition: { duration: 0.4 } },
  };

  return (
    <AnimatePresence>
      <motion.div
        className="checkout-container"
        variants={slideVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <section
          className={`checkout-section frosted ${
            basket.length === 0 ? "checkout-empty" : ""
          }`}
        >
          <h1>CHECKOUT</h1>

          {stockAdjusted && (
            <p style={{ color: "red", marginBottom: "1rem" }}>
              Some items were adjusted or removed due to updated stock availability.
            </p>
          )}

          {basket.length === 0 ? (
            <p>Your basket is empty.</p>
          ) : (
            <>
              <table className="checkout-table">
                <tbody>
                  {basket.map((item) => (
                    <tr key={item.priceId}>
                      <td className="checkout-item">
                        {item.thumbnail && <img id={`${item.name.replace(/ /g, "-")}`} src={item.thumbnail} alt={item.name} />}
                        <div className="item-details">
                          <div>{`${item.name || "Unnamed item"} - ${item.size}`}</div>
                          {item.price && (
                            <div>£{(item.price * item.quantity).toFixed(2)}</div>
                          )}
                        </div>
                      </td>
                      <td className="center">
                        <div className="quantity-controls">
                          <button
                            className={`quantity-btn ${item.quantity <= 1 ? "button-disabled" : ""}`}
                            onClick={() => decrementQuantity(item.priceId)}
                            disabled={item.quantity <= 1}
                          >
                            –
                          </button>

                          <span>{item.quantity}</span>

                          <button
                            className={`quantity-btn ${item.quantity >= (stock[item.id] || 0) ? "button-disabled" : ""}`}
                            onClick={() => incrementQuantity(item.priceId, item.id)}
                            disabled={
                              stock[item.id] !== undefined &&
                              item.quantity >= stock[item.id]
                            }
                          >
                            +
                          </button>
                        </div>
                      </td>
                      <td className="center">
                        <button
                          className="remove-btn"
                          onClick={() => removeItem(item.priceId)}
                          aria-label="Remove item"
                        >
                          <FaTrashCan />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="checkout-summary total-cost">
                Total: £{totalCost.toFixed(2)}
              </div>
            </>
          )}

          {basket.length > 0 && (
          <div className="checkout-buttons">
            <button
              className="proceed-to-checkout-btn"
              onClick={checkoutFunction}
            >
              Proceed to Checkout
            </button>
          </div>
        )}
        </section>

        <Footer />
      </motion.div>
    </AnimatePresence>
  );
}

export default Checkout;